import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useParamValidationRedirect } from "../../Hooks/useParamValidationRedirect";
import { decryptData, safeURIDecode } from "../../utils";
import isEmail from "validator/lib/isEmail";
import { Alert, Button, CircularProgress, Snackbar } from "@mui/material";
import logo from "../../Assets/Images/logo.png"
import "./Welcome.scss";

const Welcome = () => {

  const defaultErrorMessage = "An unexpected error occurred. Please try again later.";

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [errorMsg, setErrorMsg] = useState(defaultErrorMessage);
  const [alertSeverity, setAlertSeverity] = useState("error");
  const [fname, setFname] = useState("");
  const [lname, setLname] = useState("");
  const [dataObj, setDataObj] = useState({});
  const [assessData, setAssessData] = useState(null);

  

  useEffect(() => {
    // clear session storage for saved assessment answers
    sessionStorage.removeItem("assessmentAnswers");
  }, []);

  useEffect(() => {
    const encrypted = searchParams.get("data");
    const password = process.env.REACT_APP_DECRYPT_PASSWORD;
    if (encrypted) {
      decryptData(encrypted, password).then(result => {
        if (result) {
          // console.log("Decrypted:", JSON.parse(result));
          setDataObj(JSON.parse(result));
          result = JSON.parse(result);
          const fName = safeURIDecode(result?.firstname || "") || "";
          const lName = safeURIDecode(result?.lastname || "") || "";
          // console.log("fname", fName, "lname", lName);
          setFname(fName);
          setLname(lName);
        } else {
          console.warn("Failed to decrypt or invalid data");
        }
      });
    }
  }, [searchParams]);

  const showSnackbar = (msg, severity) => {
    setErrorMsg(msg);
    setAlertSeverity(severity);
    setSnackbarOpen(true);
  };

  // List of possible email param keys
  const emailParamKeys = ["email", "user", "useremail", "user_email", "utm_email"];

  // get valid email
  function getValidEmail(searchParams, dataObj) {
    // Try email from decrypted dataObj
    if (dataObj && dataObj.email && isEmail(dataObj.email)) {
      return dataObj.email;
    } else {
      console.warn("Email not found in data param object...");
      return "";
    }
    //! Note: Fallback: Try all possible email params in URL. But commented out because we are getting email only from "data" param now
    // for (const key of emailParamKeys) {
    //   const val = searchParams.get(key);
    //   if (val) {
    //     const decoded = safeURIDecode(val);
    //     if (isEmail(decoded)) {
    //       return decoded;
    //     }
    //   }
    // }
    // return "";
  }

  const emailStr = getValidEmail(searchParams, dataObj);

  // if email is valid, check if hash already exists for email OR create new user & assessment
  useEffect(() => {
    if (emailStr) {
      createAssessmentAndUser();
    }
  }, [emailStr]);

  // validate required params and redirect user to landing page if any are missing or invalid
  useParamValidationRedirect({
    searchParams,
    //! Note: if we want to get & validate email from other params, we can add them here as shown below in the commented line
    // required: [{ keys: ["email", "user", "useremail", "user_email", "utm_email"], rule: "email" }],
    required: ["email"],
    redirectUrl: "https://westfallgold.gloo.com",
    skipValidation: id && id?.length > 4, // skip validation if assessment hash (id) is in the URL
  });

  const removeDataParam = () => {
    // remove only "data" param from searchParams
    const newSearchParams = new URLSearchParams(searchParams.toString());
    newSearchParams.delete("data");
    return newSearchParams;
  }

  const nextPage = () => {
    const hash = assessData?.hash || assessData?.id || id;
    // remove only "data" param from searchParams
    const newSearchParams = removeDataParam();
    if (hash && hash?.length > 4) {
      navigate("/how-it-works/" + hash + "/?" + newSearchParams.toString(), { replace: true });
    } else {
      console.error("Invalid assessment hash:", hash);
      showSnackbar(defaultErrorMessage, "error");
    }
  }

  const goToResultsPage = (assessmentHash) => {
    const newSearchParams = removeDataParam();
    newSearchParams.set("completed", "true");
    const hash = assessmentHash || assessData?.hash || assessData?.id || id;
    navigate(`/results/${hash}/?${newSearchParams.toString()}`, { replace: true });
  };
  

  const assessmentPageId = 1052;

  const createAssessmentAndUser = () => {

    // // If an ID (user/assessment hash) is provided, navigate to the assessment page directly
    // if (id && id?.length > 4) {
    //   navigate(`/how-it-works/${id}/?${searchParams}`, {
    //     replace: true,
    //   });
    //   return;
    // }

    // If no ID (user/assessment hash) is provided, create a new assessment and user
    //setIsLoading(true);

    let userId = 0;
    let leaderId = 0;
    let isMember = false;
    if (searchParams?.get("leader")) {
      leaderId = searchParams?.get("leader");
      userId = searchParams?.get("user");
      isMember = true;
    }
    
    const assessmentId = process.env.REACT_APP_CLIENT_ID;
    let firstName = fname || "";
    let lastName = lname || "";
    let fullName = `${firstName} ${lastName}`?.trim() || "";
    let nameType = "both";
    let email = emailStr || "";
    const firstPage = "assessment"; // this if for URL returned in response
    // const firstPage = "assessment";
    const designation = "leader";
    const parentId = 0;
    let pageId = 1052;
    const phone = null;
    const deploymentId = 0;
    const returnHash = true;
    let integrationDetails = null;

    const userDetails = { fullName, firstName, lastName, email, nameType, phone };

    const payload = { assessmentId, userId, userDetails, firstPage, pageId, designation, parentId, deploymentId, returnHash };

    fetch(process.env.REACT_APP_API_URI + "/v5/user/assessmentAndUser", {
      method: "POST",
      mode: "cors",
      headers: { "Content-Type": "application/json" },
      // v3 API payload
      // body: JSON.stringify({ assessmentId, userId, isMember, leaderId, fullName, firstName, lastName, nameType, email, firstPage, pageId, integrationDetails }),
      body: JSON.stringify(payload),
    })
      .then((response) => {
        if (!response.ok) {
          const error = new Error();
          error.status = response.status;
          if (response.status >= 500) {
            error.message = "Server error: Failed to start the assessment. Please try again later.";
          } else {
            error.message = defaultErrorMessage;
          }
          throw error;
        }
        return response.json();
      })
      .then((data) => {
        // clear session storage for saved assessment answers
        sessionStorage.removeItem("assessmentAnswers");
        
        if (data.status === "failure" && data?.hash?.length > 4) {
          setAssessData(data);
          if (data?.assessmentCompleted == true) {
            goToResultsPage(data?.hash);
            return;
          }
        }

        if (data.status === "success") {
          setAssessData(data);
          setIsLoading(false);
          console.log("data", data);
        } else {
          if (data?.message?.toLowerCase() === "assessment exists") {
            console.warn("Assessment already exists for this user.");
          } else {
            showSnackbar(data?.message || defaultErrorMessage, "error");
            console.error("Failed to create new assessment...", data);
          }
        }
      })
      .catch((err) => {
        showSnackbar(err?.message || defaultErrorMessage, "error");
        console.error("Failed to create new assessment...", err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };


  return isLoading ? (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh", minHeight: "100dvh", backgroundColor: "#FFFFFF" }}>
        <CircularProgress style={{ color: "#CC972F" }} size={60} />
      </div>
  ) : (
    <div className="tg-intro-wrapper flex-container">
      <div className="top">
        <div className="tg-logo">
          <img src={logo} width={112} height={46} alt="Westfall Gold" />
        </div>
        <h1 className="tg-title">Welcome to the High-Capacity Giver&nbsp;Evaluation</h1>
        <hr className="tg-divider" />
        <p className="tg-highlight">
          Many high-capacity givers report that <strong>only 20–30% of their giving is gifted to their own&nbsp;church.</strong>
        </p>
        <p className="tg-subtext">Take this evaluation&nbsp;to:</p>
        <ul className="tg-points">
          <li className="point-item">
            <span className="dot" />
            <div className="point-text">
              <div className="text">Gauge how you engage your high-capacity&nbsp;givers</div>
            </div>
          </li>
          <li className="point-item">
            <span className="dot" />
            <div className="point-text">
              <div className="text">Learn 5 key insights to unleash giving&nbsp;potential</div>
            </div>
          </li>
        </ul>
      </div>

      <div className="mid"></div>
      <div className="bottom">
        <Button className="tg-btn" disabled={isLoading}
          onClick={nextPage}
        >
          {isLoading ? (
            <CircularProgress size={31.5} style={{ color: "#FFFFFF" }} className="loading-spinner" />
          ) : (
            "I’m Ready"
          )}
        </Button>
      </div>
      <Snackbar open={snackbarOpen} autoHideDuration={5000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert onClose={() => setSnackbarOpen(false)}
          severity={alertSeverity} sx={{ width: "100%" }}
        >
          {errorMsg}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Welcome;
